import React, { useCallback, useMemo, useRef, useState } from "react";
import styled from "styled-components";

const ListStyle = styled.div`
    gap: 8px;
    display: flex;
    flex-direction: column;
`;

// drop target of an item; the insert line sits in the gap above or below it
const SlotStyle = styled.div`
    position: relative;
    &.dragging {
        opacity: 0.5;
    }
    &.before::before,
    &.after::after {
        left: 0;
        right: 0;
        content: "";
        height: 2px;
        z-index: 1;
        position: absolute;
        background-color: ${props => props.theme.primaryLight};
    }
    &.before::before {
        top: -5px;
    }
    &.after::after {
        bottom: -5px;
    }
`;

// Vertical list whose items are reordered with native drag and drop. Every child receives a
// dragProps object to spread onto the element that serves as its handle. The dragged item goes
// before or after the item it is dropped on, depending on which half of it the pointer is in;
// onChangeOrder(fromIndex, toIndex) gets the index to splice the item in at once it has been
// taken out of the list. Without onChangeOrder the list is static.
export const SortableList = props => {

    const { onChangeOrder, children } = props;

    const slotRefs = useRef([]);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dropTarget, setDropTarget] = useState(null);

    // stable, so children that memoize their markup keep working with the dragProps they first received
    const onDragStart = useCallback(event => {
        const index = slotRefs.current.findIndex(slot => slot?.contains(event.currentTarget));
        event.dataTransfer.effectAllowed = "move";
        // Firefox does not start a drag without data
        event.dataTransfer.setData("text/plain", String(index));
        setDraggedIndex(index);
    }, []);

    const onDragEnd = useCallback(() => {
        setDraggedIndex(null);
        setDropTarget(null);
    }, []);

    const sortable = Boolean(onChangeOrder);
    const dragProps = useMemo(() => (sortable ? { draggable: true, onDragStart, onDragEnd } : {}), [sortable, onDragStart, onDragEnd]);

    const dropPosition = event => {
        const bounds = event.currentTarget.getBoundingClientRect();
        return event.clientY < bounds.top + bounds.height / 2 ? "before" : "after";
    };

    const onDragOver = (index, event) => {
        if (draggedIndex === null || index === draggedIndex) {
            return;
        }
        event.preventDefault();
        const position = dropPosition(event);
        // dragover fires continuously, so keep the previous state object while nothing changed
        setDropTarget(previous => (previous?.index === index && previous?.position === position ? previous : { index, position }));
    };

    const onDrop = (index, event) => {
        event.preventDefault();
        const fromIndex = draggedIndex;
        onDragEnd();
        if (fromIndex === null || fromIndex === index) {
            return;
        }
        const toIndex = (dropPosition(event) === "before" ? index : index + 1) - (fromIndex < index ? 1 : 0);
        if (toIndex !== fromIndex) {
            onChangeOrder(fromIndex, toIndex);
        }
    };

    const slotClassName = index => {
        if (index === draggedIndex) {
            return "dragging";
        }
        return dropTarget?.index === index ? dropTarget.position : "";
    };

    return (
        <ListStyle>
            {React.Children.toArray(children).map((child, index) =>
                <SlotStyle
                    key={child.key}
                    ref={el => (slotRefs.current[index] = el)}
                    className={slotClassName(index)}
                    onDragOver={event => onDragOver(index, event)}
                    onDrop={event => onDrop(index, event)}>
                    {React.cloneElement(child, { dragProps })}
                </SlotStyle>
            )}
        </ListStyle>
    );
};
